import type { ControlBridgeQueueItem } from '../deriveViewModel';

export type ExoWorkDeeplinkKind =
  | 'drive'
  | 'admin_resources'
  | 'go_landing'
  | 'app_route'
  | 'external';

/** Resolved “Go do work” target from an ExoPanel queue row. */
export interface ExoWorkDeeplink {
  kind: ExoWorkDeeplinkKind;
  label: string;
  /** SPA path or absolute URL. */
  href?: string;
  /** In-bridge surface switch (Drive leaf, etc.). */
  surface?: 'drive' | 'guidance' | 'diagnose' | 'exopanel';
  scope?: string;
  openItemId?: string;
}

function textBlob(item: ControlBridgeQueueItem): string {
  return [
    item.itemId,
    item.title,
    item.activityDescription,
    item.issueSummary,
    item.coordinationNoteRef,
    item.triageRoute,
    item.workHref,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function normalizeAppPath(raw: string): string | undefined {
  const text = raw.trim();
  if (!text) return undefined;
  if (/^https?:\/\//i.test(text)) return text;
  if (text.startsWith('/')) return text;
  return undefined;
}

/**
 * Prefer explicit manifest workHref / triageRoute; else heuristic routes used by
 * ExoPanel assigned-work closure (Drive leaf, Resources admin, /go landings).
 */
export function resolveExoWorkDeeplink(item: ControlBridgeQueueItem): ExoWorkDeeplink {
  const explicit =
    normalizeAppPath(item.workHref ?? '') || normalizeAppPath(item.triageRoute ?? '');
  if (explicit) {
    if (/^https?:\/\//i.test(explicit)) {
      return { kind: 'external', label: 'Open linked work', href: explicit };
    }
    if (explicit.startsWith('/admin/resources')) {
      return { kind: 'admin_resources', label: 'Open Resources admin', href: explicit };
    }
    if (explicit === '/go' || explicit.startsWith('/go/')) {
      return { kind: 'go_landing', label: 'Open landing (/go)', href: explicit };
    }
    return { kind: 'app_route', label: 'Go do work', href: explicit };
  }

  const blob = textBlob(item);
  if (
    blob.includes('admin/resources') ||
    blob.includes('resources-admin') ||
    blob.includes('resources admin') ||
    blob.includes('resource-catalog') ||
    /\bresources\b/.test(blob) && blob.includes('verify')
  ) {
    return {
      kind: 'admin_resources',
      label: 'Open Resources admin',
      href: '/admin/resources',
    };
  }

  if (
    blob.includes('source_key') ||
    blob.includes('source-key') ||
    blob.includes('/go') ||
    blob.includes('landing') ||
    blob.includes('magnet')
  ) {
    return { kind: 'go_landing', label: 'Open landing (/go)', href: '/go' };
  }

  const scope = (item.filterScope || item.domain || '').trim() || undefined;
  return {
    kind: 'drive',
    label: scope ? 'Open on Drive' : 'Open on Drive',
    surface: 'drive',
    scope,
    openItemId: item.itemId,
  };
}

/** Basename / stem for coordination-note show queries. */
export function coordinationNoteStem(ref: string | undefined): string | undefined {
  if (!ref?.trim()) return undefined;
  const cleaned = ref.trim().replace(/\\/g, '/');
  const base = cleaned.split('/').pop() || cleaned;
  return base.replace(/\.md$/i, '') || undefined;
}
